// WP-1.20 (S-03/S-44 Security): the active-session list with per-row revoke (the "this device"
// session can't revoke itself). Reuses the WP-0.6 auth sessions API.
// WP-C1.7 (ST-4, SE-1): app-lock PIN setup + auto-lock timeout — a client-side UI gate on this
// same authenticated session (see lib/appLock.tsx's own header comment), not a second login.
import { useState } from 'react';
import {
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../../components/FormKit';
import { useClearPin, usePinStatus, useSetPin } from '../../lib/appLock';
import { useSessions, useRevokeSession } from '../../lib/sessions';
import { useSettings, useSetSetting } from '../../lib/settings';

const AUTO_LOCK_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: 'Off (manual lock only)' },
  { value: 1, label: '1 minute' },
  { value: 5, label: '5 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
];

function AppLockSection(): React.JSX.Element {
  const { data: status } = usePinStatus();
  const { data: settingsData } = useSettings();
  const setPin = useSetPin();
  const clearPin = useClearPin();
  const setSetting = useSetSetting();

  const enabled = status?.enabled ?? false;
  const [showForm, setShowForm] = useState(false);
  const [pin, setPinDraft] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saved, setSaved] = useState(false);

  const autoLockMinutes =
    typeof settingsData?.appLockAutoLockMinutes === 'number'
      ? settingsData.appLockAutoLockMinutes
      : 5;
  const canSave = pin.length >= 4 && pin.length <= 6 && pin === confirmPin;

  const savePin = (): void => {
    setSaved(false);
    setPin.mutate(pin, {
      onSuccess: () => {
        setPinDraft('');
        setConfirmPin('');
        setShowForm(false);
        setSaved(true);
      },
    });
  };

  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      <h2 className="mb-3 text-sm font-medium text-ink">App lock (SE-1)</h2>
      <p className="mb-4 text-sm text-muted">
        {enabled ? 'App lock is on.' : 'App lock is off.'} A PIN separate from your account
        password locks the app after it&apos;s been idle.
      </p>

      {(!enabled || showForm) && (
        <>
          <Field label="New PIN (4-6 digits)" htmlFor="app-lock-pin">
            <input
              id="app-lock-pin"
              type="password"
              inputMode="numeric"
              className={inputClass}
              value={pin}
              onChange={(e) => {
                setPinDraft(e.target.value.replace(/\D/g, '').slice(0, 6));
              }}
            />
          </Field>
          <Field label="Confirm PIN" htmlFor="app-lock-pin-confirm">
            <input
              id="app-lock-pin-confirm"
              type="password"
              inputMode="numeric"
              className={inputClass}
              value={confirmPin}
              onChange={(e) => {
                setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6));
              }}
            />
          </Field>
          <button
            type="button"
            className={primaryButtonInlineClass}
            disabled={!canSave || setPin.isPending}
            onClick={savePin}
          >
            Save PIN
          </button>
        </>
      )}

      {enabled && !showForm && (
        <div className="flex gap-2">
          <button
            type="button"
            className={ghostButtonInlineClass}
            onClick={() => {
              setShowForm(true);
            }}
          >
            Change PIN
          </button>
          <button
            type="button"
            className={ghostButtonInlineClass}
            disabled={clearPin.isPending}
            onClick={() => {
              clearPin.mutate();
            }}
          >
            Turn off app lock
          </button>
        </div>
      )}
      {saved && <span className="ml-2 text-sm text-success">Saved</span>}

      {enabled && (
        <div className="mt-4">
          <Field label="Auto-lock after" htmlFor="app-lock-timeout">
            <select
              id="app-lock-timeout"
              className={selectClass}
              value={autoLockMinutes}
              onChange={(e) => {
                setSetting.mutate({
                  key: 'appLockAutoLockMinutes',
                  value: Number(e.target.value),
                });
              }}
            >
              {AUTO_LOCK_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}
    </section>
  );
}

export function SettingsSecurity(): React.JSX.Element {
  const { data: sessions } = useSessions();
  const revoke = useRevokeSession();

  return (
    <div className="space-y-6">
      <AppLockSection />

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium text-ink">Active sessions</h2>
        {(sessions ?? []).length === 0 ? (
          <p className="text-sm text-muted">No active sessions.</p>
        ) : (
          <ul className="divide-y divide-border">
            {(sessions ?? []).map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 py-2">
                <div>
                  <p className="text-sm text-ink">
                    {s.userAgent ?? 'Unknown device'}
                    {s.current && (
                      <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                        This device
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted">
                    Last seen {s.lastSeenAt.slice(0, 16).replace('T', ' ')}
                  </p>
                </div>
                {!s.current && (
                  <button
                    type="button"
                    className="text-sm text-danger hover:underline"
                    disabled={revoke.isPending}
                    onClick={() => {
                      revoke.mutate(s.id);
                    }}
                  >
                    Revoke
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
